import React, {useEffect, useState} from "react";
import partial from "lodash/partial";
import {useTranslation} from "@unibuddy/intl";
import {Box} from "@unibuddy/patron";
import {popupNames, sharedEvents} from "shared/AnalyticsNew/constants/productSpecification";
import useAnalytics from "shared/AnalyticsNew/hooks/useAnalytics";

import s from "./PhoneNumberToast.pcss";
import ServiceIcon from "./ServiceIcon/ServiceIcon";
import {ImageClock} from "../../../../widgets/modules/Chat/images/ImageClock";
import {PhoneInput} from "../../../../components/PhoneInput/PhoneInput";
import {IntlNamespaces} from "../../../../translations/types";
import {INTL_ADD, INTL_SMS, INTL_WHATSAPP} from "../../../../translations/chat";
import {useUniversity} from "../../../../widgets/modules/UniversityProvider/UniversityProvider";

type PhoneNumberToastProps = {
    title: string;
    showAddPhoneNumber?: boolean;
    text?: string;
    onAddPhoneNumber?: (phoneNumber: string, selectedService: string) => Promise<boolean>;
    updatingPhoneNumber?: boolean;
    hideWhatsapp?: boolean;
    isOpen?: boolean;
};

export function PhoneNumberToast({
    title,
    showAddPhoneNumber,
    text,
    onAddPhoneNumber,
    updatingPhoneNumber,
    hideWhatsapp,
    isOpen,
}: PhoneNumberToastProps) {
    const [phoneNumber, setPhoneNumber] = useState("");
    const [selectedService, setSelectedService] = useState("sms");
    const {slug: universitySlug} = useUniversity();
    const {t} = useTranslation(IntlNamespaces.CHAT);
    const {trackEvent} = useAnalytics();
    const tabIndexValue = isOpen ? 0 : -1;

    const phoneInputHandler = (value: string) => setPhoneNumber(value);

    const serviceIconHandler = (value: string) => {
        trackEvent(sharedEvents.phoneNumberCollectionServiceSelected.name, {
            universitySlug,
            popup: popupNames.phoneNumberCollection,
            cta: value,
        });
        setSelectedService(value);
    };

    const addPhoneNumberHandler = () => {
        trackEvent(sharedEvents.phoneNumberCollectionNumberAdded.name, {
            universitySlug,
            popup: popupNames.phoneNumberCollection,
            cta: "Add Phone Number",
        });
        onAddPhoneNumber?.(phoneNumber, selectedService);
    };

    useEffect(function trackPhoneNumberToastShown() {
        if (showAddPhoneNumber) {
            trackEvent(sharedEvents.phoneNumberCollectionPopupViewed.name, {
                universitySlug,
                popup: popupNames.phoneNumberCollection,
            });
        }
        // We want this called only on mount
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
        <div className={s.toast}>
            <Box display="flex" justifyContent="center" padding="medium" width="100%">
                <ImageClock height={80} />
            </Box>
            <div className={s.top}>
                <div className={s.title}>{title}</div>
            </div>
            <div className={s.text}>{text}</div>
            {showAddPhoneNumber && (
                <div className={s.phoneNumber}>
                    <div className={s.notificationType}>
                        <ServiceIcon
                            tabIndex={tabIndexValue}
                            name="sms"
                            ariaLabel={t(INTL_SMS.key, INTL_SMS.defaultValue)}
                            active={selectedService === "sms"}
                            onClick={partial(serviceIconHandler, "sms")}
                        />
                        {!hideWhatsapp && (
                            <>
                                <span className={s.orText}>or</span>
                                <ServiceIcon
                                    tabIndex={tabIndexValue}
                                    name="whatsapp"
                                    ariaLabel={t(INTL_WHATSAPP.key, INTL_WHATSAPP.defaultValue)}
                                    active={selectedService === "whatsapp"}
                                    onClick={partial(serviceIconHandler, "whatsapp")}
                                />
                            </>
                        )}
                    </div>
                    <div className={s.inputSection}>
                        <Box alignItems="center" display="flex" flexDirection="row">
                            {isOpen && (
                                <PhoneInput
                                    flexDirection="row"
                                    inputValue={phoneNumber}
                                    inputName="phoneNumber"
                                    inputLabel="Phone Number"
                                    onChange={phoneInputHandler}
                                    placeHolder="7700900775"
                                />
                            )}
                            <button
                                tabIndex={tabIndexValue}
                                data-test-id="add-phone-number"
                                disabled={updatingPhoneNumber || !selectedService}
                                className={s.addButton}
                                onClick={addPhoneNumberHandler}
                            >
                                {updatingPhoneNumber && (
                                    <span>
                                        <i
                                            className="fa fa-spinner fa-spin"
                                            aria-label="Updating"
                                        />
                                        &nbsp;
                                    </span>
                                )}
                                {t(INTL_ADD.key, INTL_ADD.defaultValue)}
                            </button>
                        </Box>
                    </div>
                </div>
            )}
        </div>
    );
}
